import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type { JSX } from 'react';
import type { DetailedHTMLProps, HTMLAttributes, ImgHTMLAttributes, InputHTMLAttributes } from 'react';
import styles from './ChatMarkdown.module.css';

function cn(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const markdownComponents: Components = {
  p: ({ children }) => <p className={styles.p}>{children}</p>,

  h1: ({ children }) => <h1 className={styles.h1}>{children}</h1>,
  h2: ({ children }) => <h2 className={styles.h2}>{children}</h2>,
  h3: ({ children }) => <h3 className={styles.h3}>{children}</h3>,
  h4: ({ children }) => <h4 className={styles.h4}>{children}</h4>,
  h5: ({ children }) => <h5 className={styles.h5}>{children}</h5>,
  h6: ({ children }) => <h6 className={styles.h6}>{children}</h6>,

  ul: ({ className, children, ...props }) => (
    <ul
      {...props}
      className={cn(styles.ul, className?.includes('contains-task-list') && styles.taskList, className)}
    >
      {children}
    </ul>
  ),

  ol: ({ className, children, ...props }) => (
    <ol {...props} className={cn(styles.ol, className)}>
      {children}
    </ol>
  ),

  li: ({ className, children, ...props }) => (
    <li
      {...props}
      className={cn(styles.li, className?.includes('task-list-item') && styles.taskItem, className)}
    >
      {children}
    </li>
  ),

  input: ({
    type,
    className,
    ...props
  }: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) =>
    type === 'checkbox' ? (
      <input {...props} type="checkbox" className={cn(styles.taskCheckbox, className)} />
    ) : (
      <input {...props} type={type} className={className} />
    ),

  code: ({
    className,
    children,
    inline,
    ...props
  }: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & { inline?: boolean }) => {
    if (inline) {
      return (
        <code className={cn(styles.codeInline, className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className={cn(styles.codeBlock, className)} {...props}>
        {children}
      </code>
    );
  },

  pre: ({ children }) => <pre className={styles.pre}>{children}</pre>,

  table: ({ children }) => (
    <div className={styles.tableWrap}>
      <table className={styles.table}>{children}</table>
    </div>
  ),

  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className={styles.tr}>{children}</tr>,
  th: ({ children }) => <th className={styles.th}>{children}</th>,
  td: ({ children }) => <td className={styles.td}>{children}</td>,

  blockquote: ({ children }) => <blockquote className={styles.blockquote}>{children}</blockquote>,

  hr: () => <hr className={styles.hr} />,

  strong: ({ children }) => <strong className={styles.strong}>{children}</strong>,
  em: ({ children }) => <em className={styles.em}>{children}</em>,
  del: ({ children }) => <del className={styles.del}>{children}</del>,

  a: ({ href, children }) => (
    <a href={href} className={styles.link} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),

  img: ({
    src,
    alt,
    title,
    className,
    ...props
  }: DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>) =>
    src ? (
      <img
        {...props}
        className={cn(styles.img, className)}
        src={src}
        alt={alt ?? ''}
        title={title ?? undefined}
        loading="lazy"
      />
    ) : null,
};

export type ChatMarkdownProps = {
  /** Raw markdown string (normalize upstream if needed). */
  children: string;
  className?: string;
};

export function ChatMarkdown({ children, className }: ChatMarkdownProps): JSX.Element {
  return (
    <div className={cn(styles.root, className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
